// The kreska: the centre of the circle and the "now" of the axis in one line,
// and on the phone the grip that sets the proportion between them (§3, §5.3).
//
// S3 owns this file: the my-lines summary (Decision 38, #89), the freshness
// reading (Decision 37, #83), the grip, geolocation and the ZTM failure states.
import { onSnapshot, type Snapshot } from '../data/data.ts';
import { freshness, type Freshness } from '../derive/freshness.ts';
import { summary } from '../derive/summary.ts';
import { derived } from '../derived.ts';
import { clockLabel } from '../shared/format.ts';
import { here } from '../shared/geo.ts';
import type { Item } from '../shared/model.ts';
import { DIAL_UNDER, fixedBoxes, mount } from '../shell.ts';
import { resolveCentre, STARY_RYNEK } from '../state/centre.ts';
import { located, returnToNow } from '../state/commands.ts';
import { memory } from '../state/memory.ts';
import { movedByHand } from '../state/moved.ts';
import { viewState } from '../state/viewstate.ts';
import { geolocate } from './geolocate.ts';

/**
 * The vehicles the kreska counts: the ones INSIDE the viewfinder, taken from
 * the same derived set „Warstwy" counts (SPEC-viewfinder §2.5).
 *
 * The two used to disagree — „Linie 16, 174, 2 pojazdy" over a circle showing
 * one — because this module counted the whole snapshot while the circle counted
 * the ring. Now neither counts: both read what one derivation decided.
 */
const inReach = (): Item[] => derived.get().derived?.summary.vehicles ?? [];

// ---- the grip (§5.3, D-h) ----

/**
 * Measured in the prototype and binding (D-h). The block runs 92…633 px; the
 * disc is the block less the 76 px of paper under it, capped at 342; under
 * 130 px of block there is no disc left worth the name and it becomes the
 * 72 px dial. At the default proportion of 0.5 that is a 363 px block with a
 * 287 px disc — the numbers `src/style.css` starts from.
 */
const BLOCK_MIN = 92;
const BLOCK_MAX = 633;
const BLOCK_RANGE = BLOCK_MAX - BLOCK_MIN;
const DISC_MAX = 342;
const DIAL_DISC = 72;
/** The paper under the disc, where S1's radius strip goes. */
const UNDER_DISC = 76;

const clamp = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Writes the circle block's height, never taking the rail below the kreska's
 * own band.
 *
 * The block's range is D-h's and the column's other boxes used to be three: the
 * list's toolbar is a fourth (SPEC-viewfinder §2.2) and „Dodaj do ekranu" a
 * fifth while it is up (#348). The rail is the one box in the column that
 * gives: at the top of the grip's range that took the rail to 53 px against an
 * 80 px band, and a scroll could then push the band off the rail's upper edge
 * entirely — the very thing `e2e/kreska.spec.ts`'s grip scenario exists to catch.
 *
 * Measured rather than subtracted from a constant, because what the column has
 * is the viewport's business and not ours, and the correction is at most the
 * toolbar's own height. The disc is untouched by it: `DISC_MAX` caps it at 342
 * px, so a block shortened by tens of pixels still holds it with the paper
 * under it.
 */
function setBlockHeight(block: HTMLElement, height: number, band: HTMLElement): void {
  // The rail is NOT measured: its height eases with this very block (§5.3), so
  // a reading taken here is the layout before the change. The other boxes in
  // the column are `fixedBoxes()`, and what they leave is the answer.
  const column = block.parentElement?.clientHeight ?? 0;
  const room = column - fixedBoxes() - band.getBoundingClientRect().height;
  block.style.height = `${Math.max(BLOCK_MIN, room > 0 ? Math.min(height, room) : height)}px`;
}

export const geometry = (grip: number) => {
  const block = Math.round(BLOCK_MIN + clamp(grip) * BLOCK_RANGE);
  const dial = block < DIAL_UNDER;
  return { block, dial, disc: dial ? DIAL_DISC : Math.min(DISC_MAX, block - UNDER_DISC) };
};

/** Below this a pointer has not moved, it has trembled, and the tap is a tap. */
const DRAG_SLOP = 4;

/**
 * A press that starts on one of these is that control's, never the grip's.
 * `⇅` is absent on purpose: it is the handle the grip is dragged by.
 */
const HANDS_OFF = 'button, a, input, select, textarea, [role="button"]';
/** One arrow key step, in block pixels, so the keyboard reaches both ends. */
const KEY_STEP = 40 / BLOCK_RANGE;

/**
 * The smallest full wheel: the block at which the disc is exactly the size of
 * the dial that replaces it. Below this the design hands the resident a 72 px
 * dial, which is *bigger* than the disc a 130 px block would give — so the band
 * between the two is a place no control should be able to stop. The keyboard
 * skips it in both directions (review 4), and dragging is continuous, as the
 * prototype's is, because a finger is choosing a size rather than a step.
 */
const FULL_MIN_GRIP = (DIAL_DISC + UNDER_DISC - BLOCK_MIN) / BLOCK_RANGE;

/**
 * One arrow step, in the axis's direction or the wheel's.
 *
 * Pure, and exported for its own test: the step table is a rule about where the
 * keyboard may land, and „never a 56 px disc announced as a wheel" is not
 * something a DOM test states well.
 */
export function gripStep(from: number, towardsAxis: boolean): number {
  if (towardsAxis) {
    // Past the smallest full wheel there is nothing between here and the dial.
    const next = from - KEY_STEP;
    return next < FULL_MIN_GRIP ? 0 : next;
  }
  // And back out of the dial, the first thing there is, is that same wheel.
  //
  // `geometry(from).dial`, not `from === 0`: a drag parks the grip anywhere
  // below the threshold while the dial is showing — 0.0102 is an ordinary place
  // to let go — and an exact-equality guard would step such a grip by the
  // ordinary amount, into a 62 px disc announced as a wheel. Smaller than the
  // dial it had just left.
  if (geometry(from).dial) return FULL_MIN_GRIP;
  return clamp(from + KEY_STEP);
}

/**
 * What the slider reports. Its value is the AXIS's share, not the wheel's, so
 * that ArrowUp increases it — the ARIA convention — and does what the finger
 * does: the kreska rides up, the axis gets the room, the wheel gives it up.
 */
export function gripValue(grip: number): { now: number; text: string } {
  const axis = 1 - clamp(grip);
  const text = geometry(grip).dial
    ? 'tarcza'
    : axis < 0.4
      ? 'więcej koła'
      : axis > 0.6
        ? 'więcej osi'
        : 'koło i oś po połowie';
  return { now: Math.round(axis * 100), text };
}
/** §5.10: on the desktop the kreska is static and the disc sizes itself in CSS. */
const DESKTOP = '(min-width: 900px)';

/** The intro's one nudge, measured in the prototype (dossier §9.1): 24 px. */
const BREATH_PX = 24;

/** The proportion the design opens on: a 363 px block with a 287 px disc (D-h). */
const DEFAULT_GRIP = 0.5;

/** Set by `mountKreska`. The band is mounted once, so there is one of these. */
let expand: (() => void) | null = null;

/**
 * „Dotknij, żeby wrócić do koła." — the dial's tap.
 *
 * The line is drawn once, beside the dial inside the circle block, and that is
 * `src/wheel.ts`'s; the proportion and what remembers it are this module's. So
 * the tap calls this rather than writing `memory.grip` itself: two writers of
 * one key drift, and a write nobody is listening to would leave the tap doing
 * nothing at all.
 */
export function expandCircle(): void {
  expand?.();
}

// ---- the band ----

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = '',
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/**
 * Builds the band once and then only writes into it. Nothing here is rebuilt on
 * a repaint, because the freshness reading is a live region and a live region
 * whose children are replaced every second announces a counter (#72).
 */
export function mountKreska(): void {
  const root = mount('kreska');
  // A named group, so that everything in the band can be reached *as being in
  // the band*. Without it the kreska's controls are only findable page-wide by
  // name, and „Linie 16, 174, …" is then indistinguishable from S2's notice row
  // „Linia 16 – objazd przez remont torowiska". The name is what the band is
  // (§3): the centre of the circle and the "now" of the axis in one.
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', 'Środek i teraz');

  const main = el('div', 'kreska__main');
  const place = el('div', 'kreska__place');

  const locate = el('button', 'kreska__locate');
  locate.type = 'button';
  // The prototype's „Stary Rynek · teraz 14:00" is one text run, with the 10 px
  // gap only between the glyph and it. Name and clock are two boxes here, so
  // that one of them may truncate and the other may not, so they go inside a
  // title of their own — a gap on `.kreska__place` would otherwise open between
  // them as well and the separator would sit visibly wide of the name.
  const title = el('div', 'kreska__title');
  const name = el('span', 'kreska__name');
  const clock = el('span', 'kreska__clock');
  title.append(name, clock);
  place.append(locate, title);

  const summaryBtn = el('button', 'kreska__summary');
  summaryBtn.type = 'button';
  // S4 remembers which control opened a screen as a selector, so focus can go
  // back to it on close (`openerKey`, `src/screens.ts:72`): it reads `id`
  // first, then `aria-label`. An id, because an `aria-label` here would replace
  // the visible sentence as this button's accessible name, which is the whole
  // of D-k.
  summaryBtn.id = 'kreska-summary';
  // One inline box holding all three parts, not three flex items: „…3 pojazdy, "
  // ends in a space, and a flex item's trailing space is collapsed away, so the
  // three would run together as „3 pojazdy,opóźnienia nieznane".
  const summaryLine = el('span', 'kreska__summary-line');
  const summaryA = el('span', 'kreska__summary-a');
  const summaryB = el('span', 'kreska__summary-b');
  const summaryC = el('span', 'kreska__summary-c');
  summaryLine.append(summaryA, summaryB, summaryC);
  summaryBtn.append(summaryLine);
  main.append(place, summaryBtn);

  const side = el('div', 'kreska__side');
  // The accessible name is the *reading*, not its value (§7.1): what changes is
  // the content, and only the coarse part of it is ever announced (#72).
  const fresh = el('div', 'kreska__fresh');
  fresh.setAttribute('role', 'status');
  fresh.setAttribute('aria-live', 'polite');
  fresh.setAttribute('aria-label', 'Świeżość danych ZTM');
  /** The one child that is ever announced. It changes when the state changes. */
  const freshSaid = el('span', 'visually-hidden');
  /** The eye's half: a dot, a sign and a number that may tick. Never announced. */
  const freshDot = el('span', 'kreska__dot');
  freshDot.setAttribute('aria-hidden', 'true');
  const freshShort = el('span', 'kreska__short');
  freshShort.setAttribute('aria-hidden', 'true');
  fresh.append(freshSaid, freshDot, freshShort);
  // Every reading is written into a text node that is created here, once, and
  // afterwards only has its `data` changed. Assigning `textContent` would drop
  // the node and insert a new one — a `childList` mutation inside a polite live
  // region, which is #72 exactly: the status would announce itself every second
  // instead of when its state changes.
  const saidText = freshSaid.appendChild(document.createTextNode(''));
  const dotText = freshDot.appendChild(document.createTextNode(''));
  const shortText = freshShort.appendChild(document.createTextNode(''));

  // A drag-only control is not a control (§8), so the grip is also a slider:
  // arrows step it and Home/End take it to either end.
  const grip = el('div', 'kreska__grip', '⇅');
  grip.setAttribute('role', 'slider');
  grip.setAttribute('aria-label', 'Proporcja koła i osi');
  grip.setAttribute('aria-orientation', 'vertical');
  grip.setAttribute('aria-valuemin', '0');
  grip.setAttribute('aria-valuemax', '100');
  grip.tabIndex = 0;
  side.append(fresh, grip);

  /** „szukam…" and „Bez dostępu do Twojej pozycji…" — one line, one voice (§5.8). */
  const note = el('p', 'kreska__note');

  root.append(main, side, note);

  // ---- state this module keeps ----

  let snapshot: Snapshot | null = null;
  let grip01 = clamp(memory.grip.get());
  let reading: Freshness | null = null;
  let tick: ReturnType<typeof setInterval> | null = null;

  const desktop = () => matchMedia(DESKTOP).matches;

  /**
   * `--disc` is the one number for the disc's diameter and the grip is what
   * moves it (§4.6). It is written as an element style rather than a rule
   * because `src/styles/kreska.css` is imported *before* `src/style.css`'s own
   * `.circle` block and would lose the cascade to it — and because on the
   * desktop the breakpoint sizes the disc itself, so the grip must write
   * nothing at all there and clear what it wrote on the way across.
   */
  function applyGeometry() {
    const block = mount('wheel');
    if (desktop()) {
      block.style.removeProperty('height');
      block.style.removeProperty('--disc');
      block.style.removeProperty('padding-bottom');
      grip.hidden = true;
      return;
    }
    grip.hidden = false;
    const { block: height, disc, dial } = geometry(grip01);
    // The intro's one "breath" (§5.9): S4 puts `data-breath` on `<html>` and the
    // block is 24 px taller while it is there, which nudges the band down and
    // back. It is a rendering of someone else's state, so it touches neither
    // `grip01` nor `memory.grip` — the proportion the resident chose is not
    // what is moving.
    const breath = document.documentElement.hasAttribute('data-breath') ? BREATH_PX : 0;
    setBlockHeight(block, height + breath, root);
    block.style.setProperty('--disc', `${disc}px`);
    // In dial mode the paper under the disc goes with the radius strip S1 hides
    // there, or a 92 px block could not hold a 72 px dial.
    if (dial) block.style.paddingBottom = '0px';
    else block.style.removeProperty('padding-bottom');

    const value = gripValue(grip01);
    grip.setAttribute('aria-valuenow', String(value.now));
    grip.setAttribute('aria-valuetext', value.text);
  }

  function setGrip(next: number, remember: boolean) {
    grip01 = clamp(next);
    applyGeometry();
    if (remember) memory.grip.set(grip01);
  }

  // ---- the drag ----

  let start: { y: number; grip: number; id: number } | null = null;
  let dragging = false;
  /** A drag has ended and the click it may or may not produce is not a tap. */
  let swallowNextClick = false;

  root.addEventListener('pointerdown', (e: PointerEvent) => {
    // A new gesture has begun, so a swallow left armed by the previous one is
    // stale: this press will produce its own click, and that one is a tap.
    swallowNextClick = false;
    if (desktop() || e.button !== 0) return;
    // A press that begins on a control belongs to that control, not to the
    // grip. The prototype guards this — its `gripDown` returns on
    // `e.target.closest('button')` — and without it a press and drag from
    // „Twoje linie" resizes the circle under the resident's thumb.
    //
    // `⇅` is deliberately not in that list: it is the handle, and dragging
    // from it is how the grip is meant to be used. It is a `role="slider"`
    // rather than a `role="button"`, so it does not match.
    if ((e.target as Element | null)?.closest(HANDS_OFF)) return;
    // Capture is deliberately **not** taken here: nothing is captured until the
    // finger has travelled, which is what keeps a tap a tap (see `endedDragAt`).
    start = { y: e.clientY, grip: grip01, id: e.pointerId };
    dragging = false;
  });

  root.addEventListener('pointermove', (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return;
    const dy = e.clientY - start.y;
    if (!dragging) {
      if (Math.abs(dy) < DRAG_SLOP) return;
      dragging = true;
      try {
        root.setPointerCapture(e.pointerId);
      } catch {
        // A pointer the browser no longer considers active — it was lifted or
        // cancelled between this event and its dispatch. The drag still tracks;
        // only the capture is lost, and losing it is not worth dropping the
        // gesture for.
      }
    }
    // 1:1 with the finger, and no snap points anywhere in the range.
    setGrip(start.grip + dy / BLOCK_RANGE, false);
  });

  function endDrag(e: PointerEvent) {
    if (!start || e.pointerId !== start.id) return;
    if (dragging) {
      if (root.hasPointerCapture(e.pointerId)) root.releasePointerCapture(e.pointerId);
      memory.grip.set(grip01);
      swallowNextClick = true;
    }
    start = null;
    dragging = false;
  }
  root.addEventListener('pointerup', endDrag);
  root.addEventListener('pointercancel', endDrag);

  /**
   * A gesture that ended in a drag is not a tap on whatever it ended over, so
   * the click it leaves behind is dropped.
   *
   * A flag cleared by the next `pointerdown`, not a `once` listener and not a
   * time window. A `once` listener would never fire after a touch drag — past
   * its own slop the browser suppresses the click entirely — and would then eat
   * the resident's *next* tap, which is the prototype's grabber bug by another
   * route. A time window would eat a tap that arrived inside it, which a quick
   * hand really can do. Clearing on the next press is exact in both directions:
   * the click a drag produces is swallowed, and a click belonging to a gesture
   * that has since begun never is.
   *
   * One case stays swallowed and is accepted: a `click` with no `pointerdown`
   * before it — a keyboard activation immediately after a touch drag, where the
   * drag left the flag armed and the browser never sent the click that would
   * have cleared it. The next activation works. Removing even that would mean
   * tracking which control the drag ended over, and eating one keypress is a
   * smaller harm than the two this flag exists to prevent.
   */
  root.addEventListener(
    'click',
    (e: Event) => {
      if (!swallowNextClick) return;
      swallowNextClick = false;
      e.stopPropagation();
      e.preventDefault();
    },
    { capture: true },
  );

  grip.addEventListener('keydown', (e: KeyboardEvent) => {
    if (desktop()) return;
    // ArrowUp and ArrowRight increase the value, which is the axis's share:
    // the kreska rides up and the wheel gives up its room, which is what the
    // same gesture does with a finger.
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') setGrip(gripStep(grip01, true), true);
    else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') setGrip(gripStep(grip01, false), true);
    // Home is the minimum of that value and End the maximum: no axis at all,
    // and no wheel at all.
    else if (e.key === 'Home') setGrip(1, true);
    else if (e.key === 'End') setGrip(0, true);
    else return;
    e.preventDefault();
  });

  // Back to the proportion the design opens on, as the prototype does — not to
  // whatever was remembered before the dial. One door, and `expandCircle` is
  // the only way through it.
  expand = () => setGrip(DEFAULT_GRIP, true);

  // ---- the two buttons ----

  locate.addEventListener('click', () => {
    if (movedByHand(viewState.get())) {
      // `◇` comes back in one tap (§3), through the command the wheel's
      // crosshair calls too — the whole of #226.
      returnToNow(located());
      return;
    }
    geolocate.ask();
  });

  summaryBtn.addEventListener('click', () => {
    viewState.set({ screen: 'linie' }, 'open');
  });

  // ---- painting ----

  function paintFreshness(now: number) {
    if (!snapshot) return;
    const next = freshness(snapshot, now);
    // Only ever the `data` of text nodes that already exist (#72).
    if (reading?.sentence !== next.sentence) saidText.data = next.sentence;
    if (reading?.short !== next.short) shortText.data = next.short;
    if (reading?.dot !== next.dot) {
      freshDot.dataset.dot = next.dot;
      dotText.data = next.dot === 'sign' ? '⊘' : '';
    }
    if (reading?.brick !== next.brick) fresh.dataset.tone = next.brick ? 'brick' : 'ink';
    reading = next;

    // The seconds are the only reading that moves by itself, so they are the
    // only one that costs a timer.
    if (next.ticking && !tick) tick = setInterval(() => paintFreshness(Date.now()), 1000);
    if (!next.ticking && tick) {
      clearInterval(tick);
      tick = null;
    }
  }

  function paint() {
    const view = viewState.get();
    const now = Date.now();
    paintFreshness(now);

    const centre = resolveCentre(view);
    const asked = geolocate.get();
    const moved = movedByHand(view);
    const me = !moved && asked === 'granted' && view.centre.kind === 'me';

    locate.textContent = moved ? '◇' : me ? '◎' : '◌';
    locate.setAttribute(
      'aria-label',
      moved ? 'Środek przeniesiony' : me ? 'Środek: Twoja pozycja' : 'Pokaż, gdzie jestem',
    );
    locate.setAttribute('aria-pressed', String(me));

    // The place name truncates in the middle of the row; the clock never does
    // (§5.3, D-6), so they are two elements and only one of them may shrink.
    name.textContent = centre.name;
    clock.textContent = view.at != null ? ` · o ${view.at}` : ` · teraz ${clockLabel(now)}`;

    // The centre was moved, so the rule above the band says so (§3).
    root.dataset.moved = String(moved);

    const parts = summary({ view, items: inReach(), ztm: reading?.kind ?? 'starting' });
    summaryA.textContent = parts.a;
    summaryB.textContent = parts.b;
    summaryC.textContent = parts.c;

    note.textContent =
      asked === 'pending'
        ? 'szukam…'
        : asked === 'refused'
          ? `Bez dostępu do Twojej pozycji. Środek: ${STARY_RYNEK.name}, odległości od niego.`
          : '';
    note.hidden = note.textContent === '';

    applyGeometry();
  }

  viewState.subscribe(paint);
  geolocate.subscribe(() => {
    // A granted position is a centre move, and a centre move is an opening
    // (§4.2): `tu=ja` goes in the address, so the view is shareable and Back
    // unwinds it like every other opening. `here.at` is already set, so
    // `resolveCentre` resolves it and the distances become „od Ciebie" (§5.8).
    //
    // `at: null` because this IS a move in space, and a move in space returns
    // the clock to its fixed now (D-p) — spelled as `src/wheel.ts` and
    // `src/detail.ts` spell it. The guard alone does not carry it: the prompt is
    // an async window, and a borrowed hour can arrive from the ADDRESS while it
    // is open — a shared `#teraz=15:00` link, or a step back onto one — and a
    // `set` is a merge, so it would ride along to „ja" (#213). The resident
    // tapped `◌`, which asks for „ja, teraz"; a link that carries both is a
    // different thing and never reaches here, because its centre is already
    // „ja" and this guard is about `default`.
    if (geolocate.get() === 'granted' && viewState.get().centre.kind === 'default') {
      viewState.set({ centre: { kind: 'me' }, at: null }, 'open');
    }
    paint();
  });
  here.subscribe(paint);
  // The breakpoint is a real one, so crossing it has to clear what the grip
  // wrote: an inline height beats the desktop media query (§5.10).
  matchMedia(DESKTOP).addEventListener('change', paint);
  // Two attributes on `<html>` this band follows: the intro's `data-breath`, which
  // it renders (§5.9), and the install band's 45 px arriving or leaving (#348).
  new MutationObserver(applyGeometry).observe(document.documentElement, {
    attributeFilter: ['data-breath', 'data-install'],
  });

  paint();

  // Every module that draws from the snapshot subscribes for itself (§4.3).
  onSnapshot((next) => {
    snapshot = next;
    paint();
  });

  // A link that arrives carrying `tu=ja`. `resume()` never prompts — it locates
  // only when the permission is already granted, and otherwise shows the note
  // (§5.8). The address may not make a browser ask; only a tap may.
  if (viewState.get().centre.kind === 'me') void geolocate.resume();
}
